import { canStartContract } from "app/utils/contractState";

// Gating rules for the Job Setup "Save & Send" checkboxes and the Job Detail
// card Send buttons. Both surfaces read the same track, so they cannot disagree
// about whether a send is possible.
//
// mode drives what a click does:
//   "toggle"      – normal checkbox (paid tier, requirements met)
//   "disabled"    – shown but not checkable (missing field, no mailbox, or
//                   state unknown)
//   "upgrade"     – Free tier; the click opens the Upgrade modal, stays unchecked
//   "stripe-hint" – paid tier without Stripe Connect; Job Setup shows a hint,
//                   Job Detail opens the Payment connection prompt

const LABELS = {
  name: "Client Name",
  email: "Client Email",
  datetime: "Inspection Date & Time",
  address: "Address",
  quote: "Quote",
};

const quoteMissing = (quote) =>
  quote === "" || quote === null || quote === undefined;

/**
 * Names the fields a contract send would be missing, in the order the form
 * shows them.
 *
 * The backend rejects a send with an incomplete client or appointment, and its
 * message names none of the fields, so the checkbox has to say which ones are
 * absent before the inspector can act on it.
 */
export const getMissingContractFields = (appointment, quote) => {
  const report = appointment?.job?.report || {};
  const client = report.client || {};
  const address = report.address || {};
  const missing = [];
  if (!client.name) missing.push(LABELS.name);
  if (!client.email) missing.push(LABELS.email);
  if (!appointment?.start_time) missing.push(LABELS.datetime);
  if (!(address.address_line1 || address.city)) missing.push(LABELS.address);
  if (quoteMissing(quote)) missing.push(LABELS.quote);
  return missing;
};

/**
 * The same, for a payment request: a Stripe link needs somewhere to send it and
 * an amount to charge, and nothing else on the job.
 */
export const getMissingPaymentFields = (appointment, quote) => {
  const client = appointment?.job?.report?.client || {};
  const missing = [];
  if (!client.email) missing.push(LABELS.email);
  if (quoteMissing(quote)) missing.push(LABELS.quote);
  return missing;
};

/**
 * Whether Save & Send may offer a contract at all.
 *
 * True only where a send would create a NEW agreement: pristine, revoked or
 * reset, and the two dead ends (declined, delivery_failed) whose only recovery
 * is a fresh contract. A live contract is managed from Job Detail instead, and
 * offering a send for one means the inspector clicks and gets a 409.
 */
export const isContractCheckboxVisible = (status) => canStartContract(status);

/**
 * Whether Save & Send may offer a payment request.
 *
 * Pristine, plus the two settled outcomes — the design's "Restart Payment" on a
 * refunded or lost-dispute job is this same send. An open dispute is excluded:
 * the money is still contested, and a second link would be charging twice for
 * one inspection.
 */
export const isPaymentCheckboxVisible = (status) =>
  status === "no_payment" || status === "refunded" || status === "dispute_lost";

// "Client Name is required" / "Client Name and Quote are required" — the hint
// names the fields, since the checkbox itself already says what it would send.
const requiredTooltip = (missing) => {
  if (missing.length === 1) return `${missing[0]} is required`;
  const head = missing.slice(0, -1).join(", ");
  return `${head} and ${missing[missing.length - 1]} are required`;
};

// The Contract Sent email is the client's only signing invite: BoldSign's own
// emails are switched off (backend #243). Without a connected mailbox the send
// reaches no one, so the track blocks it rather than warning.
export const NO_EMAIL_ALERT =
  "No email account connected. Connect Google or Microsoft 365 in User Information to send contracts. The client receives the signing link only by email.";

const NO_INVITE = "the client won't get the signing link by email.";

/**
 * Why a connected inspector's contract email still would not go out, or null.
 * The backend only reports this after the send (contract_email.skip_reason),
 * so it is checked up front. It warns without blocking until product decides
 * whether a switched-off or empty template should block like a missing
 * mailbox (AS-917 Q3).
 */
export const getContractEmailWarning = ({ contractEmailTemplate }) => {
  if (!contractEmailTemplate) return null;
  if (!contractEmailTemplate.is_active)
    return `Contract email turned off: ${NO_INVITE}`;
  if (!contractEmailTemplate.body?.trim())
    return `Contract email is empty: ${NO_INVITE}`;
  return null;
};

/**
 * Decides what the contract checkbox and the Contract card's Send button do.
 *
 * Every branch here exists because the alternative is a click that fails: a
 * send the backend 409s, a Free-tier send it rejects outright, a send with a
 * field the contract text needs and the job does not have, or a send that
 * creates a contract no client is ever invited to sign.
 *
 * emailConnected defaults to true so a caller that never checked the mailbox
 * keeps the old behaviour; useJobSetupSend always passes the real answer.
 */
export const computeContractTrack = ({
  status,
  tier,
  appointment,
  quote,
  emailConnected = true,
  emailLoading = false,
  emailWarning = null,
}) => {
  if (!isContractCheckboxVisible(status)) return { show: false };
  if (tier === "Free")
    return { show: true, mode: "upgrade", disabled: false, autoCheck: false };
  // Same reason as stripeLoading: until GET /scheduling/providers/ answers, a
  // connected inspector looks unconnected, and the alert would flash.
  if (emailLoading)
    return {
      show: true,
      mode: "disabled",
      disabled: true,
      autoCheck: false,
      tooltip: "Checking your email account",
    };
  // Ahead of the missing fields: filling them in would still send nothing.
  if (!emailConnected)
    return {
      show: true,
      mode: "disabled",
      disabled: true,
      autoCheck: false,
      tooltip: NO_EMAIL_ALERT,
      alert: NO_EMAIL_ALERT,
    };
  const missing = getMissingContractFields(appointment, quote);
  if (missing.length)
    return {
      show: true,
      mode: "disabled",
      disabled: true,
      autoCheck: false,
      tooltip: requiredTooltip(missing),
    };
  return {
    show: true,
    mode: "toggle",
    disabled: false,
    autoCheck: true,
    ...(emailWarning && { tooltip: emailWarning, warning: emailWarning }),
  };
};

/**
 * The same for the payment request, with the Stripe Connect account as an extra
 * precondition: without a connected account there is nothing to collect into,
 * and the backend refuses the send.
 *
 * Pass stripeLoading while that account is still being fetched. Skipping it
 * makes a connected inspector look unconnected for the first frames, and the
 * page then asks them to connect an account they already have.
 */
export const computePaymentTrack = ({
  status,
  tier,
  stripeConnected,
  stripeLoading = false,
  appointment,
  quote,
}) => {
  if (!isPaymentCheckboxVisible(status)) return { show: false };
  if (tier === "Free")
    return { show: true, mode: "upgrade", disabled: false, autoCheck: false };
  // Until GET /payments/collect/account/ answers, a connected inspector and a
  // never-onboarded one look identical. Offering the connect hint here tells
  // half of them to set up an account they already have, so hold the control
  // instead of guessing.
  if (stripeLoading)
    return {
      show: true,
      mode: "disabled",
      disabled: true,
      autoCheck: false,
      tooltip: "Checking your payment account",
    };
  if (!stripeConnected)
    return {
      show: true,
      mode: "stripe-hint",
      disabled: false,
      autoCheck: false,
    };
  const missing = getMissingPaymentFields(appointment, quote);
  if (missing.length)
    return {
      show: true,
      mode: "disabled",
      disabled: true,
      autoCheck: false,
      tooltip: requiredTooltip(missing),
    };
  return { show: true, mode: "toggle", disabled: false, autoCheck: true };
};
